import { useCallback, useEffect, useState } from 'react';

import {
  listBuyerRequests,
  STATUS_DB_TO_UI,
  type BuyerRequestRowJoined,
  type BuyerRequestType,
  type BuyerRequestsTabFilter,
} from '@/lib/db/buyer';
import type { RequestStatusUi } from '@/components/RequestStatusPill';
import { formatAed } from '@/lib/format';

// ---------------------------------------------------------------------------
// UI request row — denormalised, ready-to-render shape
// ---------------------------------------------------------------------------

export type RequestTypeUi = 'INTEREST' | 'CALLBACK';

const TYPE_DB_TO_UI: Record<BuyerRequestType, RequestTypeUi> = {
  interest: 'INTEREST',
  callback: 'CALLBACK',
};

export type BuyerRequestRowUi = {
  id: string;
  referenceId: string;
  dealTitle: string;
  zone: string;
  priceLabel: string;
  discountPct: number;
  dldConfidence: string | null;
  dldScope: string | null;
  type: RequestTypeUi;
  status: RequestStatusUi;
  date: string; // ISO YYYY-MM-DD (locale-agnostic for V1)
  // `whatsappEnabled` mirrors the mock contract: a follow-up CTA only makes
  // sense when the buyer request is still attached to a published deal.
  whatsappEnabled: boolean;
};

export function mapRequestRowToUi(row: BuyerRequestRowJoined): BuyerRequestRowUi {
  const deal = row.deals;
  return {
    id: row.id,
    referenceId: row.reference_id,
    dealTitle: deal?.title ?? '—',
    zone: deal?.zone ?? '—',
    priceLabel: deal?.asking_price ? formatAed(Number(deal.asking_price)) : '—',
    discountPct: deal?.discount_pct ?? 0,
    dldConfidence: deal?.dld_confidence ?? null,
    dldScope: deal?.dld_scope ?? null,
    type: TYPE_DB_TO_UI[row.type],
    status: STATUS_DB_TO_UI[row.status],
    date: row.created_at.slice(0, 10),
    whatsappEnabled: deal !== null,
  };
}

// ---------------------------------------------------------------------------
// useBuyerRequests — list with tab filter, refetches on tab change
// ---------------------------------------------------------------------------

export type BuyerRequestsState =
  | { status: 'loading'; requests: null; error: null }
  | { status: 'success'; requests: BuyerRequestRowUi[]; error: null }
  | { status: 'error'; requests: null; error: Error };

const INITIAL_STATE: BuyerRequestsState = { status: 'loading', requests: null, error: null };

export function useBuyerRequests(tab: BuyerRequestsTabFilter): {
  state: BuyerRequestsState;
  refresh: () => void;
} {
  const [state, setState] = useState<BuyerRequestsState>(INITIAL_STATE);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState(INITIAL_STATE);

    listBuyerRequests(tab)
      .then((rows) => {
        if (cancelled) return;
        const mapped = rows.map(mapRequestRowToUi);
        setState({ status: 'success', requests: mapped, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          requests: null,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [tab, tick]);

  return { state, refresh };
}
